import { XMarkIcon } from "@heroicons/react/24/outline";
import { fontMap } from "../utils/common";

/**
 * AppModal — shared glassmorphism dialog used across the redesigned journey.
 *
 * Layout matches the Figma "Modals Interaction" frames:
 *   glass-dimmed backdrop → white rounded card → title + × close →
 *   body (children) → footer (secondary gray + primary orange buttons).
 *
 * Use `title` + `children` + primary/secondary props for the standard alert
 * pattern, or pass `children` only for a fully custom body (e.g. the pre-call
 * checklist), keeping the same shell and backdrop.
 */
export default function AppModal({
  open,
  title,
  children,
  onClose, // optional; renders the × when provided
  primaryText,
  onPrimary,
  primaryDisabled = false,
  primaryIcon: PrimaryIcon,
  secondaryText,
  onSecondary,
  selectedLang,
  maxWidth = "max-w-md",
  showDivider = false,
}) {
  if (!open) return null;

  const fontFamilyCSS = fontMap[selectedLang?.lang] || "Lato";
  const hasFooter = Boolean(primaryText || secondaryText);

  return (
    <div
      style={{ zIndex: 1000, fontFamily: fontFamilyCSS }}
      className="fixed inset-0"
      role="dialog"
      aria-modal="true"
      aria-labelledby={title ? "app-modal-title" : undefined}
    >
      {/* Glassmorphism backdrop */}
      <div
        className="absolute inset-0 bg-black-150/40 backdrop-blur-sm"
        aria-hidden="true"
        onClick={onClose}
      />

      <div className="absolute inset-0 flex min-h-full items-center justify-center p-3 sm:p-4">
        {/* Height-capped flex column so tall content (e.g. the pre-call
            checklist on an iPhone SE) scrolls in the body while the title and
            footer stay pinned, instead of clipping off-screen. maxHeight uses
            dvh (accounts for mobile browser chrome) with a vh class fallback. */}
        <div
          className={`relative flex max-h-[90vh] w-full ${maxWidth} flex-col overflow-hidden rounded-3xl bg-white shadow-xl`}
          style={{ maxHeight: "90dvh" }}
          onClick={(e) => e.stopPropagation()}
        >
          {(title || onClose) && (
            <div className="flex shrink-0 items-start justify-between gap-3 px-5 pt-5 sm:px-6 sm:pt-6">
              {title && (
                <h2
                  id="app-modal-title"
                  className="mb-0 text-xl font-bold text-black-150 sm:text-2xl"
                >
                  {title}
                </h2>
              )}
              {onClose && (
                <button
                  type="button"
                  aria-label="Close"
                  onClick={onClose}
                  className="-mr-1 -mt-1 ml-auto shrink-0 rounded-full p-1 text-gray-400 hover:bg-gray-100 hover:text-gray-600"
                >
                  <XMarkIcon className="h-6 w-6" />
                </button>
              )}
            </div>
          )}

          {children && (
            <div className="mt-3 flex-1 overflow-y-auto px-5 pb-1 text-base leading-relaxed text-gray-150 sm:px-6">
              {children}
            </div>
          )}

          {showDivider && (
            <div className="mx-5 mt-4 shrink-0 border-t border-gray-275 sm:mx-6" />
          )}

          {hasFooter && (
            <div className="flex shrink-0 items-center justify-end gap-3 px-5 pb-5 pt-4 sm:px-6 sm:pb-6">
              {secondaryText && (
                <button
                  type="button"
                  onClick={onSecondary || onClose}
                  className="rounded-lg bg-gray-100 px-5 py-2.5 text-base font-semibold text-black-150 hover:bg-gray-200"
                >
                  {secondaryText}
                </button>
              )}
              {primaryText && (
                <button
                  type="button"
                  onClick={onPrimary}
                  disabled={primaryDisabled}
                  className="flex items-center gap-1 rounded-lg bg-orange-150 px-5 py-2.5 text-base font-semibold text-white hover:bg-orange-150/90 disabled:cursor-not-allowed disabled:opacity-50"
                >
                  {primaryText}
                  {PrimaryIcon && <PrimaryIcon className="h-5 w-5" />}
                </button>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}